import { useState, useEffect } from 'react';
import { api } from '../api/client';

// Master categories and subcategories - centralized single source of truth
const CATEGORY_SUBCATEGORY_MAP = {
  "Buckets": ["Flat Bucket", "Round Bucket", "Hook Bucket"],
  "Bowls": ["Non-Skid Bowl", "Regular Bowl"],
  "Cheese Chews": ["Regular", "Flavored"],
  "Wood": ["Coffee Wood", "Olive Wood"],
  "Dog Toys": ["Leather & Wool Toy", "Leather & Cotton Toy"],
  "Pet Supplies": ["Dog Toys"],
  // ============================================================
  // COMPONENT-BASED INVENTORY: New categories for product types
  // ============================================================
  "Dog Food": ["Dog Chew", "Dog Treat", "Dog Food"],
  "Raw Materials": [
    "Buffalo Cheek Chips",
    "Chick-Chips",
    "Cheese Chews",
    "Buffalo Hooves",
    "Buffalo Ears",
    "Horn",
    "Horn Core",
    "Cheek Roll",
    "Cheek Roll Rings",
    "Trachea",
    "Femur Bone",
    "Paddywack",
    "Knee Caps",
    "Tail",
    "Olive Wood",
    "Coffee Wood",
    "Leather",
    "Bone",
    "Bully Stick",
    "Rib Bone",
    "Large Femur Bone",
    "Tail Jerky",
    "Udder Bites",
    "Trachea Bites",
    "Bladder Twist",
    "Tendon",
    "Multipack",
    "Goat Horn",
    "Other"
  ]
};

// Master categories list - derived from map keys
const MASTER_CATEGORIES = Object.keys(CATEGORY_SUBCATEGORY_MAP).map(cat => ({
  value: cat,
  label: cat
}));

/**
 * Custom hook for managing category data
 * Provides centralized categories across the entire application
 * 
 * @returns {Object} { categories, loading, error, addCategory, refreshCategories }
 */
export const useCategoryData = () => {
  const [categories, setCategories] = useState(MASTER_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch categories from API (if backend endpoint exists)
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
      
      // Try to fetch from backend
      const response = await api.get(`/api/v1/categories?tenant_id=${tenantId}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      if (response.data && Array.isArray(response.data)) {
        // Map API response to expected format {value, label}
        const mappedCategories = response.data.map(cat => ({
          value: cat.name || cat.value || cat.id,
          label: cat.name || cat.label || cat.id,
          id: cat.id
        }));
        setCategories(mappedCategories);
      }
    } catch (err) {
      // If API doesn't exist yet, use master list
      console.log('Using master categories list (API not available)');
      setCategories(MASTER_CATEGORIES);
    } finally {
      setLoading(false);
    }
  };

  // Add a new category
  const addCategory = async (newCategory) => {
    const categoryObj = typeof newCategory === 'string' 
      ? { value: newCategory, label: newCategory }
      : newCategory;
    
    // Add to local state immediately
    setCategories(prev => [...prev, categoryObj]);
    
    // Try to save to backend
    try {
      const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
      await api.post('/api/v1/categories', {
        name: categoryObj.label,
        value: categoryObj.value,
        tenant_id: tenantId
      }, {
        headers: { 'X-Tenant-ID': tenantId }
      });
    } catch (err) {
      console.log('Category saved locally (backend save not available)');
    }
    
    return categoryObj;
  };

  // Refresh categories from API
  const refreshCategories = () => {
    fetchCategories();
  };

  // Fetch on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    addCategory,
    refreshCategories
  };
};

/**
 * Get categories as simple array of strings (for backwards compatibility)
 */
export const useCategoryList = () => {
  const { categories } = useCategoryData();
  return categories.map(cat => cat.value || cat.label || cat);
};

/**
 * Get categories as options array for select components
 */
export const useCategoryOptions = () => {
  const { categories } = useCategoryData();
  return categories.map(cat => ({
    value: cat.value || cat.label || cat,
    label: cat.label || cat.value || cat
  }));
};

/**
 * Get subcategories for a given category
 * @param {string} category - The category name
 * @returns {Array<string>} Array of subcategory names
 */
export const getSubcategoriesForCategory = (category) => {
  return CATEGORY_SUBCATEGORY_MAP[category] || [];
};

/**
 * Get all valid category-subcategory combinations
 * @returns {Array<{category: string, subcategory: string}>}
 */
export const getAllCategorySubcategoryPairs = () => {
  const pairs = [];
  Object.keys(CATEGORY_SUBCATEGORY_MAP).forEach(category => {
    CATEGORY_SUBCATEGORY_MAP[category].forEach(subcategory => {
      pairs.push({ category, subcategory });
    });
  });
  return pairs;
};

/**
 * Validate if a category-subcategory combination is valid
 * @param {string} category - The category name
 * @param {string} subcategory - The subcategory name
 * @returns {boolean}
 */
export const isValidCategorySubcategory = (category, subcategory) => {
  const validSubcategories = CATEGORY_SUBCATEGORY_MAP[category];
  return validSubcategories && validSubcategories.includes(subcategory);
};
